import React, { useState, useRef, useEffect } from 'react';
import { Box, Typography, styled } from '@mui/material';
import { AutoAwesome as AIIcon } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import LOGIN_TYPE from '../../constants/loginType';
import URL from '../../constants/urls';
import NEXT_ROUTES from '../../../next.routes';
import UI from '../../constants/ui';

const ServicesTrigger = styled(Box)(({ theme }) => ({
  position: 'relative',
  display: 'inline-flex',
  alignItems: 'center',
  padding: theme.spacing(1, 2),
  borderRadius: theme.shape.borderRadius,
  transition: 'background-color 0.2s',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const DropdownContainer = styled(Box)(({ theme, role, open }) => ({
  position: 'absolute',
  top: '100%',
  left: '50%',
  transform: open ? 'translate(-50%, 0)' : 'translate(-50%, -10px)',
  zIndex: 1300,
  marginTop: theme.spacing(0.5),
  minWidth: role ? 300 : 800,
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: '0 10px 30px rgba(0,0,0,0.1), 0 4px 12px rgba(0,0,0,0.05)',
  background: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  opacity: open ? 1 : 0,
  visibility: open ? 'visible' : 'hidden',
  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  pointerEvents: open ? 'auto' : 'none',
}));

const MenuItem = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5, 2),
  borderRadius: theme.shape.borderRadius,
  margin: theme.spacing(0.5, 1),
  transition: 'all 0.2s ease',
  '& a': {
    textDecoration: 'none',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    cursor: 'pointer',
    color: theme.palette.text.primary,
  },
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    transform: 'translateX(4px)',
    '& a': {
      color: theme.palette.text.primary,
    },
  },
}));

const SectionHeader = styled(Typography)(({ theme }) => ({
  padding: theme.spacing(2, 2),
  fontWeight: 600,
  fontSize: '1rem',
  color: theme.palette.text.primary,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  backgroundColor: theme.palette.grey[50],
  margin: theme.spacing(1),
  marginBottom: theme.spacing(1.5),
  borderRadius: theme.shape.borderRadius,
  textAlign: 'center',
  border: `1px solid ${theme.palette.divider}`,
}));

const MenuContainer = styled(Box)(() => ({
  display: 'flex',
  width: '100%',
  minHeight: '300px',
}));

const MenuSection = styled(Box)(({ theme }) => ({
  flex: 1,
  padding: theme.spacing(2),
  '&:not(:last-child)': {
    borderRight: `1px solid ${theme.palette.divider}`,
  },
}));

const DropdownIcon = styled(Box)(({ theme, open }) => ({
  marginLeft: theme.spacing(0.5),
  fontSize: '0.75rem',
  transition: 'transform 0.2s ease',
  transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
  color: theme.palette.text.secondary,
}));

const ServiceIcon = styled(Box)(() => ({
  fontSize: '1rem',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 24,
  height: 24,
}));

const useHoverWithDelay = (enterDelay = 0, leaveDelay = 100) => {
  const [isHovered, setIsHovered] = useState(false);
  const enterTimeoutRef = useRef(null);
  const leaveTimeoutRef = useRef(null);

  const handleMouseEnter = () => {
    if (leaveTimeoutRef.current) {
      clearTimeout(leaveTimeoutRef.current);
      leaveTimeoutRef.current = null;
    }

    enterTimeoutRef.current = setTimeout(() => {
      setIsHovered(true);
    }, enterDelay);
  };

  const handleMouseLeave = () => {
    if (enterTimeoutRef.current) {
      clearTimeout(enterTimeoutRef.current);
      enterTimeoutRef.current = null;
    }

    leaveTimeoutRef.current = setTimeout(() => {
      setIsHovered(false);
    }, leaveDelay);
  };

  const forceClose = () => {
    if (enterTimeoutRef.current) {
      clearTimeout(enterTimeoutRef.current);
      enterTimeoutRef.current = null;
    }
    if (leaveTimeoutRef.current) {
      clearTimeout(leaveTimeoutRef.current);
      leaveTimeoutRef.current = null;
    }
    setIsHovered(false);
  };

  const cleanup = () => {
    if (enterTimeoutRef.current) {
      clearTimeout(enterTimeoutRef.current);
    }
    if (leaveTimeoutRef.current) {
      clearTimeout(leaveTimeoutRef.current);
    }
  };

  useEffect(() => cleanup, []);

  return {
    isHovered,
    handleMouseEnter,
    handleMouseLeave,
    forceClose,
    cleanup,
  };
};

export default function ServicesMenu({ role }) {
  const { isHovered, handleMouseEnter, handleMouseLeave, forceClose } =
    useHoverWithDelay(0, 150);

  const handleLinkClick = () => {
    forceClose();
  };

  // Define services for both sections
  const employerServices = [
    { to: URL.FIND_CANDIDATES, label: UI.SERVICES_FIND_TALENT, icon: '👥' },
    {
      to: NEXT_ROUTES.EMPLOYER_JOB_POSTING,
      label: UI.SERVICES_JOB_POSTING,
      icon: '📄',
    },
    { to: NEXT_ROUTES.EMPLOYER_ASSESSMENT, label: UI.ASSESSMENTS, icon: '📊' },
    {
      to: NEXT_ROUTES.EMPLOYER_INTERVIEW,
      label: UI.SERVICES_AI_INTERVIEWS,
      icon: '📝',
    },
    { to: NEXT_ROUTES.EMPLOYER_EVENTS, label: UI.EVENTS, icon: '📅' },
  ];

  const candidateServices = [
    { to: URL.FIND_JOBS, label: UI.SERVICES_FIND_JOBS, icon: '💼' },
    {
      to: NEXT_ROUTES.CANDIDATE_ASSESSMENTS,
      label: UI.ASSESSMENTS,
      icon: '✅',
    },
    { to: NEXT_ROUTES.CANDIDATE_EVENTS, label: UI.EVENTS, icon: '📅' },
    {
      to: NEXT_ROUTES.CANDIDATE_MOCKINTERVIEW,
      label: UI.MOCK_INTERVIEWS,
      icon: '🎤',
    },
    {
      to: URL.AI_TOOLS,
      label: UI.AI_SERVICES,
      icon: <AIIcon sx={{ color: '#667eea' }} />,
    },
  ];

  const universityServices = [
    {
      to: NEXT_ROUTES.UNIVERSITY_CONNECT,
      label: UI.SERVICES_UNIVERSITY_CONNECT,
      icon: '🎓',
    },
  ];

  return (
    <ServicesTrigger
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      marginTop={role ? 0 : -10}
      marginLeft={2}
    >
      <Typography fontSize={16} component="span">
        {UI.SERVICES_MAIN_TITLE}
      </Typography>
      <DropdownIcon open={isHovered}>
        <svg width="12" height="8" viewBox="0 0 12 8" fill="currentColor">
          <path
            d="M1 1.5l5 5 5-5"
            stroke="currentColor"
            strokeWidth="1.5"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </DropdownIcon>

      <DropdownContainer
        role={role}
        open={isHovered}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <MenuContainer>
          {/* Candidates Section */}
          {(!role || role === LOGIN_TYPE.CANDIDATE) && (
            <MenuSection>
              {!role && (
                <SectionHeader
                  variant="subtitle2"
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    padding: '4px 8px',
                    marginBottom: '4px',
                    textTransform: 'uppercase',
                    color: 'text.primary',
                  }}
                >
                  {UI.SERVICES_FOR_CANDIDATES}
                </SectionHeader>
              )}
              {candidateServices.map((service) => {
                const { to, label, icon } = service;
                return (
                  <MenuItem key={to}>
                    <Link to={to} onClick={handleLinkClick}>
                      <ServiceIcon>{icon}</ServiceIcon>
                      {label}
                    </Link>
                  </MenuItem>
                );
              })}
            </MenuSection>
          )}

          {/* Employers Section */}
          {(!role || role === LOGIN_TYPE.EMPLOYER) && (
            <MenuSection>
              {!role && (
                <SectionHeader
                  variant="subtitle2"
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    padding: '4px 8px',
                    marginBottom: '4px',
                    textTransform: 'uppercase',
                    color: 'text.primary',
                  }}
                >
                  {UI.SERVICES_FOR_EMPLOYERS}
                </SectionHeader>
              )}
              {employerServices.map(({ to, label, icon }) => (
                <MenuItem key={to}>
                  <Link to={to} onClick={handleLinkClick}>
                    <ServiceIcon>{icon}</ServiceIcon>
                    {label}
                  </Link>
                </MenuItem>
              ))}
            </MenuSection>
          )}

          {/* Universities Section */}
          {(!role || role === LOGIN_TYPE.UNIVERSITY) && (
            <MenuSection>
              {!role && (
                <SectionHeader
                  variant="subtitle2"
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    padding: '4px 8px',
                    marginBottom: '4px',
                    textTransform: 'uppercase',
                    color: 'text.primary',
                  }}
                >
                  {UI.SERVICES_FOR_UNIVERSITIES}
                </SectionHeader>
              )}
              {universityServices.map(({ to, label, icon }) => (
                <MenuItem key={to}>
                  <Link to={to} onClick={handleLinkClick}>
                    <ServiceIcon>{icon}</ServiceIcon>
                    {label}
                  </Link>
                </MenuItem>
              ))}
            </MenuSection>
          )}
        </MenuContainer>
      </DropdownContainer>
    </ServicesTrigger>
  );
}

ServicesMenu.propTypes = {
  role: PropTypes.string,
};

ServicesMenu.defaultProps = {
  role: undefined,
};
